'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { Background, BackgroundVariant, type ColorMode, Controls, type Edge as FlowEdge, type Node as FlowNode,
// MiniMap,
type NodeTypes, Panel, ReactFlow, useEdgesState, useNodesState, useReactFlow } from '@xyflow/react';
import dynamic from 'next/dynamic';
import '@xyflow/react/dist/style.css';
import { CircleHelpIcon, TrophyIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMounted } from '@/hooks/use-mounted';
import { getLocaleDir } from '@/i18n/locale';
import type { Locale } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { usePlaygroundParams } from '../_hooks/use-playground-params';
import { usePlaygroundTour } from '../_hooks/use-playground-tour';
import { generateGraphFromData } from '../_lib/graph-generator';
import { applyHierarchicalLayout, applyRadialLayout } from '../_lib/graph-layout';
import { usePlaygroundStore } from '../_providers/playground';
import { useQuizStore } from '../_providers/quiz';
import { getPlaygroundDataOpt } from '../_queries/playground';
import { GraphFilter } from './graph-filter';
import { GraphLegend } from './graph-legend';
import { MobileUnsupported } from './mobile-unsupported';
import { ArticulationAreaNode, ArticulationPointNode } from './node-articulation';
import { LetterNode } from './node-letter';
import { TraitGroupNode, TraitNode } from './node-trait';

/**
 * Lazy load Quiz component - only shown when dialog is open
 */
const Quiz = dynamic(() => import('./quiz').then(mod => mod.Quiz), {
  ssr: false
});

/**
 * Lazy load GraphDownload - not critical for initial render
 */
const GraphDownload = dynamic(() => import('./graph-download').then(mod => mod.GraphDownload), {
  ssr: false
});

// Register custom node types
const nodeTypes: NodeTypes = {
  letter: LetterNode,
  articulationArea: ArticulationAreaNode,
  articulationPoint: ArticulationPointNode,
  traitGroup: TraitGroupNode,
  trait: TraitNode
};
export function Playground() {
  const $ = _c(118);
  const t = useTranslations("playground_page");
  const locale = useLocale() as Locale;
  let t0;
  if ($[0] !== locale) {
    t0 = getLocaleDir(locale);
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const dir = t0;
  const isMaxMd = useMediaQuery("(width < 48rem)");
  const {
    theme
  } = useTheme();
  const {
    isMounted
  } = useMounted();
  const colorMode = (isMounted ? theme : "light") as ColorMode;
  let t1;
  if ($[2] !== colorMode) {
    t1 = {
      theme: colorMode
    };
    $[2] = colorMode;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    startTour
  } = usePlaygroundTour(t1);
  const viewMode = usePlaygroundStore(_temp);
  const layoutMode = usePlaygroundStore(_temp2);
  const hierarchyDirection = usePlaygroundStore(_temp3);
  const selectedLetterId = usePlaygroundStore(_temp4);
  const selectedNodeId = usePlaygroundStore(_temp5);
  const edgeStyle = usePlaygroundStore(_temp6);
  const connectedNodeIds = usePlaygroundStore(_temp7);
  const connectedEdgeIds = usePlaygroundStore(_temp8);
  const setViewMode = usePlaygroundStore(_temp9);
  const setLayoutMode = usePlaygroundStore(_temp10);
  const setHierarchyDirection = usePlaygroundStore(_temp11);
  const setSelectedLetterId = usePlaygroundStore(_temp12);
  const setEdgeStyle = usePlaygroundStore(_temp13);
  const updateHighlight = usePlaygroundStore(_temp14);
  let t2;
  if ($[4] !== edgeStyle.shape || $[5] !== hierarchyDirection || $[6] !== layoutMode || $[7] !== selectedLetterId || $[8] !== viewMode) {
    t2 = {
      viewMode,
      layoutMode,
      hierarchyDirection,
      edgeShape: edgeStyle.shape,
      selectedLetterId
    };
    $[4] = edgeStyle.shape;
    $[5] = hierarchyDirection;
    $[6] = layoutMode;
    $[7] = selectedLetterId;
    $[8] = viewMode;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== setEdgeStyle) {
    t3 = shape => setEdgeStyle({
      shape
    });
    $[10] = setEdgeStyle;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== setHierarchyDirection || $[13] !== setLayoutMode || $[14] !== setSelectedLetterId || $[15] !== setViewMode || $[16] !== t2 || $[17] !== t3) {
    t4 = {
      currentSettings: t2,
      onViewModeChange: setViewMode,
      onLayoutModeChange: setLayoutMode,
      onHierarchyDirectionChange: setHierarchyDirection,
      onEdgeShapeChange: t3,
      onLetterSelect: setSelectedLetterId
    };
    $[12] = setHierarchyDirection;
    $[13] = setLayoutMode;
    $[14] = setSelectedLetterId;
    $[15] = setViewMode;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  usePlaygroundParams(t4);
  const isQuizDialogOpen = useQuizStore(_temp15);
  const isQuizStarted = useQuizStore(_temp16);
  const openQuizDialog = useQuizStore(_temp17);
  let t5;
  if ($[19] !== openQuizDialog) {
    t5 = () => {
      openQuizDialog();
    };
    $[19] = openQuizDialog;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleOpenQuizDialog = t5;
  const {
    fitView
  } = useReactFlow();
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const [nodes, setNodes, onNodesChange] = useNodesState(t6);
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const [edges, setEdges, onEdgesChange] = useEdgesState(t7);
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getPlaygroundDataOpt();
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const {
    data: playgroundData
  } = useQuery(t8);
  let t9;
  if ($[24] !== playgroundData?.letters) {
    t9 = playgroundData?.letters ?? [];
    $[24] = playgroundData?.letters;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const letters = t9;
  let t10;
  if ($[26] !== playgroundData?.letterArticulations) {
    t10 = playgroundData?.letterArticulations ?? [];
    $[26] = playgroundData?.letterArticulations;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const letterArticulations = t10;
  let t11;
  if ($[28] !== playgroundData?.letterTraits) {
    t11 = playgroundData?.letterTraits ?? [];
    $[28] = playgroundData?.letterTraits;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const letterTraits = t11;
  let t12;
  if ($[30] !== playgroundData?.articulationAreas) {
    t12 = playgroundData?.articulationAreas ?? [];
    $[30] = playgroundData?.articulationAreas;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const articulationAreas = t12;
  let t13;
  if ($[32] !== playgroundData?.articulationPoints) {
    t13 = playgroundData?.articulationPoints ?? [];
    $[32] = playgroundData?.articulationPoints;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const articulationPoints = t13;
  let t14;
  if ($[34] !== playgroundData?.traits) {
    t14 = playgroundData?.traits ?? [];
    $[34] = playgroundData?.traits;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const traits = t14;
  let t15;
  if ($[36] !== playgroundData?.traitGroups) {
    t15 = playgroundData?.traitGroups ?? [];
    $[36] = playgroundData?.traitGroups;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const traitGroups = t15;
  let t16;
  if ($[38] !== articulationAreas || $[39] !== articulationPoints || $[40] !== edgeStyle.isAnimated || $[41] !== edgeStyle.shape || $[42] !== fitView || $[43] !== hierarchyDirection || $[44] !== layoutMode || $[45] !== letterArticulations || $[46] !== letterTraits || $[47] !== letters || $[48] !== selectedLetterId || $[49] !== setEdges || $[50] !== setNodes || $[51] !== traitGroups || $[52] !== traits || $[53] !== viewMode) {
    t16 = () => {
      const data = {
        letters,
        letterArticulations,
        letterTraits,
        articulationAreas,
        articulationPoints,
        traits,
        traitGroups
      };
      const updateLayout = async function updateLayout() {
        const {
          nodes: rawNodes,
          edges: newEdges
        } = generateGraphFromData(data, {
          viewMode,
          layoutMode,
          hierarchyDirection,
          selectedId: selectedLetterId
        });
        let layoutedNodes;
        if (layoutMode === "radial") {
          layoutedNodes = applyRadialLayout(rawNodes, 600, 400);
        } else {
          layoutedNodes = applyHierarchicalLayout(rawNodes, newEdges, {
            direction: hierarchyDirection,
            nodeSpacing: 30,
            layerSpacing: viewMode === "letter" ? 200 : 600
          });
        }
        const nodesWithSourceEdge = new Set(newEdges.map(_temp18));
        const nodesWithTargetEdge = new Set(newEdges.map(_temp19));
        const nodesWithLayoutInfo = layoutedNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            layoutMode,
            hierarchyDirection,
            hasSourceEdge: nodesWithSourceEdge.has(node.id),
            hasTargetEdge: nodesWithTargetEdge.has(node.id)
          }
        }));
        const edgesWithStyle = newEdges.map(edge => ({
          ...edge,
          type: edgeStyle.shape,
          animated: edgeStyle.isAnimated
        }));
        setNodes(nodesWithLayoutInfo);
        setEdges(edgesWithStyle);
        setTimeout(() => {
          fitView({
            padding: 0.2,
            duration: 300
          });
        }, 50);
      };
      updateLayout();
    };
    $[38] = articulationAreas;
    $[39] = articulationPoints;
    $[40] = edgeStyle.isAnimated;
    $[41] = edgeStyle.shape;
    $[42] = fitView;
    $[43] = hierarchyDirection;
    $[44] = layoutMode;
    $[45] = letterArticulations;
    $[46] = letterTraits;
    $[47] = letters;
    $[48] = selectedLetterId;
    $[49] = setEdges;
    $[50] = setNodes;
    $[51] = traitGroups;
    $[52] = traits;
    $[53] = viewMode;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== edgeStyle.shape || $[56] !== hierarchyDirection || $[57] !== layoutMode || $[58] !== selectedLetterId || $[59] !== viewMode) {
    t17 = [viewMode, layoutMode, hierarchyDirection, edgeStyle.shape, selectedLetterId];
    $[55] = edgeStyle.shape;
    $[56] = hierarchyDirection;
    $[57] = layoutMode;
    $[58] = selectedLetterId;
    $[59] = viewMode;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  useEffect(t16, t17);
  let t18;
  if ($[61] !== connectedNodeIds || $[62] !== nodes || $[63] !== selectedNodeId) {
    let t19;
    if ($[65] !== connectedNodeIds || $[66] !== selectedNodeId) {
      t19 = node_0 => {
        const isSelected = node_0.id === selectedNodeId;
        const isConnected = connectedNodeIds.has(node_0.id);
        const shouldHighlight = isSelected || isConnected;
        const shouldDim = selectedNodeId ? !shouldHighlight : false;
        const currentHighlight = node_0.data?.isHighlighted ?? false;
        const currentDim = node_0.data?.isDimmed ?? false;
        if (currentHighlight === shouldHighlight && currentDim === shouldDim && node_0.hidden !== true) {
          return node_0;
        }
        return {
          ...node_0,
          hidden: false,
          data: {
            ...node_0.data,
            isHighlighted: shouldHighlight,
            isDimmed: shouldDim
          }
        };
      };
      $[65] = connectedNodeIds;
      $[66] = selectedNodeId;
      $[67] = t19;
    } else {
      t19 = $[67];
    }
    t18 = nodes.map(t19);
    $[61] = connectedNodeIds;
    $[62] = nodes;
    $[63] = selectedNodeId;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  const highlightedNodes = t18;
  let t19;
  if ($[68] !== connectedEdgeIds || $[69] !== edgeStyle.isAnimated || $[70] !== edges || $[71] !== selectedNodeId) {
    let t20;
    if ($[73] !== connectedEdgeIds || $[74] !== edgeStyle.isAnimated || $[75] !== selectedNodeId) {
      t20 = edge_0 => {
        const isConnected_0 = connectedEdgeIds.has(edge_0.id);
        const targetStrokeWidth = isConnected_0 ? 3 : 1;
        const targetOpacity = selectedNodeId ? isConnected_0 ? 1 : 0.2 : 1;
        const targetAnimated = isConnected_0 ? true : edgeStyle.isAnimated;
        const currentStyle = edge_0.style as Record<string, unknown> | undefined;
        if (edge_0.hidden !== true && currentStyle?.strokeWidth === targetStrokeWidth && currentStyle?.opacity === targetOpacity && edge_0.animated === targetAnimated) {
          return edge_0;
        }
        return {
          ...edge_0,
          hidden: false,
          style: {
            ...edge_0.style,
            strokeWidth: targetStrokeWidth,
            opacity: targetOpacity
          },
          animated: targetAnimated
        };
      };
      $[73] = connectedEdgeIds;
      $[74] = edgeStyle.isAnimated;
      $[75] = selectedNodeId;
      $[76] = t20;
    } else {
      t20 = $[76];
    }
    t19 = edges.map(t20);
    $[68] = connectedEdgeIds;
    $[69] = edgeStyle.isAnimated;
    $[70] = edges;
    $[71] = selectedNodeId;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  const highlightedEdges = t19;
  const highlightTimeoutRef = useRef(null);
  let t20;
  if ($[77] !== edges || $[78] !== selectedNodeId || $[79] !== updateHighlight) {
    t20 = (_event, node_1) => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        const newSelectedId = selectedNodeId === node_1.id ? null : node_1.id;
        updateHighlight(newSelectedId, edges);
      }, 100);
    };
    $[77] = edges;
    $[78] = selectedNodeId;
    $[79] = updateHighlight;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  const handleNodeClick = t20;
  let t21;
  if ($[81] !== edges || $[82] !== updateHighlight) {
    t21 = () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        updateHighlight(null, edges);
      }, 100);
    };
    $[81] = edges;
    $[82] = updateHighlight;
    $[83] = t21;
  } else {
    t21 = $[83];
  }
  const handlePaneClick = t21;
  const memoizedLetters = letters;
  let t22;
  if ($[84] !== edgeStyle.isAnimated || $[85] !== edgeStyle.shape) {
    t22 = {
      type: edgeStyle.shape,
      animated: edgeStyle.isAnimated
    };
    $[84] = edgeStyle.isAnimated;
    $[85] = edgeStyle.shape;
    $[86] = t22;
  } else {
    t22 = $[86];
  }
  const defaultEdgeOptions = t22;
  const isUnsupportedMobileView = isMaxMd && viewMode !== "letter";
  let t23;
  if ($[87] !== colorMode || $[88] !== defaultEdgeOptions || $[89] !== dir || $[90] !== edgeStyle || $[91] !== handleNodeClick || $[92] !== handleOpenQuizDialog || $[93] !== handlePaneClick || $[94] !== hierarchyDirection || $[95] !== highlightedEdges || $[96] !== highlightedNodes || $[97] !== isQuizStarted || $[98] !== isUnsupportedMobileView || $[99] !== layoutMode || $[100] !== memoizedLetters || $[101] !== onEdgesChange || $[102] !== onNodesChange || $[103] !== selectedLetterId || $[104] !== setEdgeStyle || $[105] !== setHierarchyDirection || $[106] !== setLayoutMode || $[107] !== setSelectedLetterId || $[108] !== setViewMode || $[109] !== startTour || $[110] !== t || $[111] !== viewMode) {
    t23 = !isQuizStarted && <div id="tour-canvas" className="h-[calc(100dvh-49px)]"><ReactFlow dir={dir} proOptions={{
        hideAttribution: true
      }} colorMode={colorMode} fitView={true} fitViewOptions={{
        padding: 0.2
      }} nodes={isUnsupportedMobileView ? [] : highlightedNodes} edges={isUnsupportedMobileView ? [] : highlightedEdges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} nodeTypes={nodeTypes} onNodeClick={handleNodeClick} onPaneClick={handlePaneClick} defaultEdgeOptions={defaultEdgeOptions} onlyRenderVisibleElements={true} edgesFocusable={false} zoomOnDoubleClick={false} autoPanOnNodeDrag={false} autoPanOnNodeFocus={false} minZoom={0.1}><Panel position={dir === "ltr" ? "top-left" : "top-right"} className={cn("m-0! flex w-full flex-col gap-2 p-3 sm:w-auto", dir === "rtl" && "items-end")}><GraphFilter dir={dir} viewMode={viewMode} layoutMode={layoutMode} hierarchyDirection={hierarchyDirection} edgeStyle={edgeStyle} letters={memoizedLetters} selectedLetterId={selectedLetterId} onViewModeChange={setViewMode} onLayoutModeChange={setLayoutMode} onHierarchyDirectionChange={setHierarchyDirection} onEdgeStyleChange={setEdgeStyle} onLetterSelect={setSelectedLetterId} />{!isUnsupportedMobileView && <GraphLegend dir={dir} viewMode={viewMode} />}</Panel>{!isUnsupportedMobileView && <><Panel position={dir === "ltr" ? "bottom-left" : "bottom-right"} className={cn("m-0! flex flex-col gap-2 p-3", dir === "ltr" ? "items-start" : "items-end")}><Button id="tour-quiz" dir={dir} size="sm" onClick={handleOpenQuizDialog} className="bg-linear-to-r from-accent-sky to-accent-sky-dark text-white"><TrophyIcon className="size-4" />{t("quiz.trigger")}</Button><GraphDownload /><Button id="tour-help" dir={dir} variant="outline" size="sm" onClick={startTour} className="bg-background!"><CircleHelpIcon className="size-4" />{t("tour.help_button")}</Button></Panel><Panel id="tour-controls" position={dir === "ltr" ? "bottom-right" : "bottom-left"} className="m-3!"><Controls className="static! m-0!" /></Panel></>}<Background color="var(--flow-bg-pattern)" bgColor="var(--flow-bg-canvas)" className="rounded-lg" variant={BackgroundVariant.Dots} gap={20} size={1} />{isUnsupportedMobileView && <MobileUnsupported onSwitchToLetter={() => setViewMode("letter")} />}</ReactFlow></div>;
    $[87] = colorMode;
    $[88] = defaultEdgeOptions;
    $[89] = dir;
    $[90] = edgeStyle;
    $[91] = handleNodeClick;
    $[92] = handleOpenQuizDialog;
    $[93] = handlePaneClick;
    $[94] = hierarchyDirection;
    $[95] = highlightedEdges;
    $[96] = highlightedNodes;
    $[97] = isQuizStarted;
    $[98] = isUnsupportedMobileView;
    $[99] = layoutMode;
    $[100] = memoizedLetters;
    $[101] = onEdgesChange;
    $[102] = onNodesChange;
    $[103] = selectedLetterId;
    $[104] = setEdgeStyle;
    $[105] = setHierarchyDirection;
    $[106] = setLayoutMode;
    $[107] = setSelectedLetterId;
    $[108] = setViewMode;
    $[109] = startTour;
    $[110] = t;
    $[111] = viewMode;
    $[112] = t23;
  } else {
    t23 = $[112];
  }
  let t24;
  if ($[113] !== isQuizDialogOpen) {
    t24 = isQuizDialogOpen && <Quiz />;
    $[113] = isQuizDialogOpen;
    $[114] = t24;
  } else {
    t24 = $[114];
  }
  let t25;
  if ($[115] !== t23 || $[116] !== t24) {
    t25 = <>{t23}{t24}</>;
    $[115] = t23;
    $[116] = t24;
    $[117] = t25;
  } else {
    t25 = $[117];
  }
  return t25;
}
function _temp19(e_0) {
  return e_0.target;
}
function _temp18(e) {
  return e.source;
}
function _temp17(s_15) {
  return s_15.openDialog;
}
function _temp16(s_14) {
  return s_14.isStarted;
}
function _temp15(s_13) {
  return s_13.isDialogOpen;
}
function _temp14(s_12) {
  return s_12.updateHighlight;
}
function _temp13(s_11) {
  return s_11.setEdgeStyle;
}
function _temp12(s_10) {
  return s_10.setSelectedLetterId;
}
function _temp11(s_9) {
  return s_9.setHierarchyDirection;
}
function _temp10(s_8) {
  return s_8.setLayoutMode;
}
function _temp9(s_7) {
  return s_7.setViewMode;
}
function _temp8(s_6) {
  return s_6.connectedEdgeIds;
}
function _temp7(s_5) {
  return s_5.connectedNodeIds;
}
function _temp6(s_4) {
  return s_4.edgeStyle;
}
function _temp5(s_3) {
  return s_3.selectedNodeId;
}
function _temp4(s_2) {
  return s_2.selectedLetterId;
}
function _temp3(s_1) {
  return s_1.hierarchyDirection;
}
function _temp2(s_0) {
  return s_0.layoutMode;
}
function _temp(s) {
  return s.viewMode;
}